import type { TFunction } from "i18next";
import type { Tour } from "shepherd.js";

import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";

import ButtonIcon from "../../images/rebrandIcons/ButtonIcon.png";

function createTourButtonHTML(label: string, direction: "left" | "right" | null = null, t: TFunction): string {
  const text = t(label);
  const arrowLeft = `<img class="h-2.5 mr-3 mb-1 transform rotate-180" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  const arrowRight = `<img class="h-2.5 ml-3 mb-1" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  return `
    <div class="flex gap-1 items-center">
      ${direction === "left" ? arrowLeft : ""}
      <p>${text}</p>
      ${direction === "right" ? arrowRight : ""}
    </div>
  `;
}

function useWritersTour() {
  const { t } = useTranslation(["tutorial", "onboarding", "common", "writers"]);
  const tourRef = useRef<Tour | null>(null);

  const startTour = useCallback(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "bg-white font-sentric rounded-[10px] text-gray-800 shadow-xl p-6",
        cancelIcon: { enabled: true },
        scrollTo: { behavior: "smooth", block: "center" },
      },
    });

    tourRef.current = tour;

    tour.addStep({
      id: "writersTableTour",
      text: t("tutorial:writers-table-description"),
      attachTo: { element: "#writersTable", on: "left" },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: () => tour.next(),
        },
      ],
    });

    tour.addStep({
      id: "writersAddNewButtonTour",
      text: t("tutorial:writers-add-new-writer-button"),
      attachTo: { element: "#btnWriterAddNew", on: "bottom" },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: () => tour.back(),
        },
        {
          text: createTourButtonHTML("onboarding:complete-button", null, t),
          action: () => {
            localStorage.setItem("WritersTourCompleted", "true");
            tour.complete();
          },
        },
      ],
    });

    void tour.start();
    return tour;
  }, [t]);

  const endTour = useCallback(() => {
    if (tourRef.current?.isActive()) {
      tourRef.current.complete();
    }
  }, []);

  return { startTour, endTour };
}

export default useWritersTour;
